import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type { GeoMap } from "../model/GeoMap";
import type { ILayerView } from "../model/Layer";
import type { Map } from "../map";

const es = global.es;

/** @class es.module.[resource]
 * Object providing [resource].
 *
 * NB!! Contains rest resources which have application code but must be accessible to
 * esWebAPI in order to facilitate collaboration testing.
 */
export default class AppResources {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        this.eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.cache = null;
        });

        this.updateWorkingCopy = this.updateWorkingCopy.bind(this);
        this.updateIfConfigurable = this.updateIfConfigurable.bind(this);
        this.saveBookmarkAsNew = this.saveBookmarkAsNew.bind(this);
        this.saveBookmark = this.saveBookmark.bind(this);
        this.saveChangedLayerViews = this.saveChangedLayerViews.bind(this);
        this.mapCriteria = this.mapCriteria.bind(this);
        this.createBuffers = this.createBuffers.bind(this);
        this.createBuffer = this.createBuffer.bind(this);
        this.createLayerView = this.createLayerView.bind(this);
        this.updateGeomap = this.updateGeomap.bind(this);
        this.updateLayerview = this.updateLayerview.bind(this);
        this.setWorkingCopyChanged = this.setWorkingCopyChanged.bind(this);
    }

    // TODO: eliminate this. it should be done as part of the publish/save/push method
    /**
     * 
     * @param tempGeoMap 
     * @param parentGeoMap 
     * 
     * @returns 
     */
    private updateWorkingCopy(tempGeoMap: GeoMap, parentGeoMap: GeoMap) {
        if (!tempGeoMap.$type.endsWith('TempGeoMap')) {
            throw new Error("updateWorkingCopy : Attempted to updateWorkingCopy but map ref passed is of type " + JSON.stringify(tempGeoMap));
        }
        
        return es.api.geoMap.get(parentGeoMap.id, {noCache: true}).then((newParentGeoMap) => {
            const overWrite = {
                title: newParentGeoMap.title,
                parentId: newParentGeoMap.id,
                parentLastModified: newParentGeoMap.lastModified,
                changed: false
            };

            return this.updateIfConfigurable(tempGeoMap, overWrite);
        });
    }

    /**
     * Update the geoMap on the server if the user as entitlement to do so
     * Fail silently.
     * 
     * @param geoMap
     * @param overWrite The attribute to update
     * 
     * @returns Updated geomap
     */
    private updateIfConfigurable(geoMap: GeoMap, overWrite) {
        return es.api.security.isConfigurableGeoMap(geoMap.id).then((isConfigurable) => {
            if (!isConfigurable) {
                return geoMap;
            }
            
            return es.api.geoMap.get(geoMap.id).then((savedStateGeoMap) => {
                $.extend(savedStateGeoMap, overWrite);
                return es.api.geoMap.update(savedStateGeoMap);
            });
        });
    }

    /**
     * Create a new map with all the current state saved in.
     * Will not save any of the current geoMap state except from the viewPoint
     * 
     * @param geoMapMeta
     * @param layerModel
     * @param overWrite
     * 
     * @returns {Promise.<GeoMap>} The newly created map
     */
    saveBookmarkAsNew(geoMapMeta, layerModel, overWrite) {
        const originalGeoMap = geoMapMeta.getGeoMap();
        
        let geoMap = es.util.object.deepCopy(originalGeoMap);
        $.extend(geoMap, overWrite);

        let parentGeoMap = es.util.object.deepCopy(geoMap);

        // Save as master geomap
        parentGeoMap.$type = "GeoMap";
        parentGeoMap.id = -1;
        delete parentGeoMap.activeLayerViewId;
        delete parentGeoMap.parentId;
        delete parentGeoMap.parentLastModified;
        delete parentGeoMap.changed;

        return es.api.geoMap.create(parentGeoMap, false).then((parentGeoMap) => {
            geoMap.changed = true;
            geoMap.parentId = parentGeoMap.id;
            geoMap.parentLastModified = parentGeoMap.lastModified;
            return this.updateGeomap(geoMap).then((geoMap) => {
                return Promise.all([
                    this.saveChangedLayerViews(geoMap.id, null, geoMapMeta, layerModel),
                    geoMapMeta.isTerritory() ? esApp.controls.territories.save() : null // TODO: fix esApp.controls ref
                ]).then(() => {
                    return geoMap;
                });
            }).then((geoMap) => {
                let msgTimeout;
                if(geoMapMeta.isTerritory()){
                    msgTimeout = window.setTimeout(() => {
                        $().loadingOverlay('stop', undefined, undefined, () => {
                            $().loadingOverlay('start', $i18n("Save_Msg_Territory_Publish"));
                        });
                    }, 2500);
                }
                
                let updateRecentMapsDfd;
                if (geoMapMeta.isTerritory() || originalGeoMap.parentId == null || originalGeoMap.parentId == -1) { // checking if not temp map, just in case
                    // Territories don't reuse TempMap so no need to "special case" update recent
                    updateRecentMapsDfd = es.util.deferred.resolved();
                } else {
                    // TOR9-14565 Set the current one as most recent, followed by the original's master copy (as current is the original temp map overwritten)
                    updateRecentMapsDfd = es.control.userSettings.updateMostRecentMaps([geoMapMeta.getId(), originalGeoMap.parentId]);
                }
                
                return $.when(
                    es.api.geoMap.publishWorkingCopy(geoMap.id, geoMap.parentLastModified),
                    updateRecentMapsDfd
                ).then((parentGeoMap) => {
                    return this.updateWorkingCopy(geoMap, parentGeoMap);
                }).always(() => {
                    if (msgTimeout) {
                        window.clearTimeout(msgTimeout);
                    }
                });
            });
        });
    };

    /**
     * Save the current instance of the map.
     *
     * @param geoMapMeta
     * @param layerModel
     * @param mapOverwrite
     * @param tenantProfile
     * @param isForceOverwrite
     * 
     * @returns {Promise<GeoMap>} Either the actual map, or a new one if created.
     */
    saveBookmark(geoMapMeta, layerModel, mapOverwrite, tenantProfile: string, isForceOverwrite: boolean) {
        let geoMap = geoMapMeta.getGeoMap();
        $.extend(geoMap, mapOverwrite);
        if (tenantProfile === 'FREE') {
            geoMap.indexable = true;
        }

        // We update both the temp GeoMap and the original
        return es.api.geoMap.get(geoMap.parentId, {noCache: true}).then((parentGeoMap) => {
            return this.updateGeomap(geoMap, true).then((geoMap) => {
                return Promise.all([
                    this.saveChangedLayerViews(geoMap.id, null, geoMapMeta, layerModel),
                    geoMapMeta.isTerritory() ? esApp.controls.territories.save() : null // TODO: fix esApp.controls ref
                ]).then(() => {
                    return geoMap;
                });
            }).then((geoMap) => {
                if (isForceOverwrite) {
                    // This acknowledges that we know we are overwriting and will prevent the server throwing an ex
                    //  -provided someone else hasn't modified the geomap in the meantime.
                    geoMap.parentLastModified = parentGeoMap.lastModified;
                }

                var msgTimeout;
                if(geoMapMeta.isTerritory()){
                    msgTimeout = window.setTimeout(() => {
                        $().loadingOverlay('stop', undefined, undefined, () => {
                            $().loadingOverlay('start', $i18n("Save_Msg_Territory_Publish"));
                        });
                    }, 2500);
                }

                return es.api.geoMap.publishWorkingCopy(geoMap.id, geoMap.parentLastModified).then((newParentGeoMap) => {
                    return this.updateWorkingCopy(geoMap, newParentGeoMap);
                }).always(() => {
                    if (msgTimeout) {
                        window.clearTimeout(msgTimeout);
                    }
                });
            });
        });
    }

    /**
     * Updates layerviews that have unsaved (client-side) changes. This happens when a shared map has over-rides and then user chooses to Save or Save As.
     * Example over-rides are criteria, thematics, layer order.
     * For "Save As" there is a new map and new layer views, therefore geoMapId and layerViewIdMapping must be populated.
     * For Save, map and layerviews are the same, therefore  geoMapId and layerViewIdMapping may be undefined.
     * 
     * @param geoMapId
     * @param layerViewIdMapping
     * @param geoMapMeta
     * @param layerModel
     * 
     * @returns 
     */
    saveChangedLayerViews(geoMapId: number | undefined, layerViewIdMapping, geoMapMeta, layerModel) {
        if (geoMapId === undefined) {
            geoMapId = geoMapMeta.getId();
        }

        return es.api.layerView.getByGeoMap(geoMapId).then((savedLayerViews) => {
            let dfds = [];
            const layerViewIds = layerModel.getLayerViewIds();
            for (let i = 0; i < layerViewIds.length; i++) {
                const layerviewObj = layerModel.getById(layerViewIds[i]);

                if (layerviewObj.needsExplicitRequest()){
                    const layerViewJSON = layerviewObj.getLayerView();

                    const lvIDMapping = (layerViewIdMapping === undefined ||layerViewIdMapping === null) ? layerViewIds[i] : layerViewIdMapping[layerViewIds[i]];

                    let savedLayerView = savedLayerViews.find(lv => lv.id == lvIDMapping);
                    savedLayerView.attributeNames = layerViewJSON.attributeNames;
                    savedLayerView.dynamicAttributes = layerViewJSON.dynamicAttributes;
                    savedLayerView.styleSettings = layerViewJSON.styleSettings;
                    savedLayerView.filter = layerViewJSON.filter;
                    savedLayerView.criteria = this.mapCriteria(layerViewJSON.criteria, layerViewIdMapping);
                    savedLayerView.sortOrder = layerViewJSON.sortOrder;
                    savedLayerView.ordering = layerViewJSON.ordering;
                    savedLayerView.enabled = layerViewJSON.enabled;

                    dfds.push(es.api.layerView.update(savedLayerView));
                }
            }
            
            if (dfds.length == 0) {
                return es.util.deferred.resolved();
            }
            
            return Promise.all(dfds).then(() => {
                return es.util.deferred.resolved();
            });
        });
    }

    /**
     * 
     * @param criteria 
     * @param layerViewIdMapping 
     * 
     * @returns 
     */
    private mapCriteria(criteria, layerViewIdMapping) {
        if (criteria && layerViewIdMapping && criteria.layerViewId && criteria.layerViewId in layerViewIdMapping) {
            criteria.layerViewId = layerViewIdMapping[criteria.layerViewId];
        }

        return criteria;
    }

    /**
     * 
     * @param layerModule 
     * @param bufferProps 
     * 
     * @returns 
     */
    createBuffers(layerModule, bufferProps) {
        const createAllBuffer = bufferProps.type === "Radius" ? es.api.buffer.createAllRadius : es.api.buffer.createAllDriveTime;

        return this.createLayerView(layerModule, bufferProps.type, bufferProps.feature.layerViewId).then((layerViewId, newLayerCreated) => {
            const feature = bufferProps.feature;
            const zones = bufferProps.zones.map((item) => parseFloat(item.value));
            const unit = bufferProps.unit;
            const criteria = bufferProps.criteria;

            return createAllBuffer(layerViewId, feature.layerViewId, zones, criteria, unit).then((taskStatus) => {
                return es.util.deferred.resolved(layerViewId, newLayerCreated, taskStatus);
            });
        });
    }

    /**
     * 
     * @param layerModule 
     * @param bufferProps 
     * 
     * @returns 
     */
    createBuffer(layerModule, bufferProps) {
        const createBuffer = bufferProps.type === "Radius" ? es.api.buffer.createRadius : es.api.buffer.createDriveTime;

        return this.createLayerView(layerModule, bufferProps.type, bufferProps.feature.layerViewId, bufferProps.colorPerFeature).then((layerViewId, newLayerCreated) => {
            const feature = bufferProps.feature;
            const zones = bufferProps.zones.map((item) => parseFloat(item.value));
            const unit = bufferProps.unit;

            return createBuffer(layerViewId,feature.layerViewId, feature.id, zones, unit).then((bufferIds) => {
                return es.util.deferred.resolved(layerViewId, newLayerCreated, bufferIds);
            });
        });
    }

    /**
     * 
     * @param layerModule 
     * @param type 
     * @param sourceId 
     * 
     * @returns 
     */
    createLayerView(layerModule, type: string, sourceId: number, colorPerFeature?: boolean) {
        const getLayer = type === "Radius" ? es.util.map.getMapScopeRadiusLayerViewModel : es.util.map.getMapScopeDriveTimeLayerViewModel;
        const createLayer = type === "Radius" ? es.util.map.addRadiusBufferLayerview : es.util.map.addDriveTimeBufferLayerview;

        const layer = getLayer(layerModule);
        if (layer) {
            return es.util.deferred.resolved(layer.getId(), false);
        }
        
        return es.api.layerView.get(sourceId).then((sourceLayerView) => {
            return createLayer(sourceLayerView.geoMapId, sourceLayerView.styleSettings.defaultStyle.color, colorPerFeature);
        }).then((layerView) => {
            return es.util.deferred.resolved(layerView.id, true);
        });
    }

    /**
     * Wrapper around app save map method.
     * 
     * @param geoMap to save
     * @param isSaveViewOnly if only saving view point then do not set the tempGeoMap.changed attr to true
     * 
     * @returns
     */
    updateGeomap(geoMap: GeoMap, isSaveViewOnly?: boolean) {
        es.api.getEventChannel().fireEvent("onBeforeUpdateGeomap", geoMap, !!isSaveViewOnly);

        if (geoMap.$type.endsWith('TempGeoMap') && isSaveViewOnly !== true) {
            geoMap.changed = true;
        }
        es.log.log("updateGeomap", geoMap, isSaveViewOnly, geoMap.changed ? "set geoMap.changed = true" : "");
        return es.api.geoMap.update(geoMap);
    }

    /**
     * Save layerview if it is private
     * 
     * @param layerView 
     * @param map 
     * @returns 
     */
    updateLayerview(layerView: ILayerView, map: Map) {
        es.api.getEventChannel().fireEvent("onBeforeUpdateLayerView", layerView);

        if (map.meta.getShareStatus() == "PRIVATE" || map.meta.getType() === "TerritoryGeoMap" ||
            (es.isEmbeddedInSFDC &&  map.meta.getShareStatus() == "SHARED_TEMPLATE")) {
            //es.log.info("SAVE LAYERVIEW ID=" + layerView.id + " - CONDITIONAL LOGIC : GEOMAP ID " + layerView.geoMapId + " IS " + map.meta.getShareStatus() + " SO **WILL** BE UPDATED", layerView);
            return es.api.layerView.update(layerView).then((layerView) => {
                if (!map.meta.isTemp()) {
                    return layerView;
                }
                
                // TOR9-15019 - meta module eagerly updates the changed property, even if not stored on the server. Must load from server (or cache) to be sure.
                return es.api.geoMap.get(map.meta.getId()).then((geoMap) => {
                    if (geoMap.changed) {
                        return layerView;
                    }
                    
                    return this.updateGeomap(geoMap, false).then((geoMap) => {
                        return layerView;
                    });
                });
            });
        }
        
        // map is shared publicly or within org so we don't auto save
        // es.log.info("SAVE LAYERVIEW ID=" + layerView.id + " - CONDITIONAL LOGIC : GEOMAP ID " + layerView.geoMapId + " IS "  + map.meta.getShareStatus() + " SO WILL **NOT** BE UPDATED");
        return es.util.deferred.resolved(layerView);
    }

    /**
     * Set the geoMap.changed attribute for certain events. 
     * 
     * @param geoMapId 
     * @returns 
     */
    setWorkingCopyChanged(geoMapId: number) {
        //console.log("setWorkingCopyChanged for " + geoMapId + "...");
        return es.api.geoMap.get(geoMapId).then((geoMap) => {
            if (geoMap.changed || es.isPublicAccess) { // if it's already set as changed or it's a public map, then don't bother updating again
                return es.util.deferred.resolved(geoMap);
            }

            return this.updateGeomap(geoMap, false).then((geoMap) => {
                return geoMap;
            });
        });
    }
}
